import { isOutOfSpec } from '../../../shared/utils/isOutOfSpec.ts';
import { sortBy } from '../../../shared/utils/sortUtils.ts';
import { RecordFinal, RecordWithProtocol } from '../../types/sharedTypeImpl.ts';
import { PreviewTable, RecordRow } from './types.ts';


export function createPreviewTable(_records: RecordWithProtocol[]): PreviewTable {
    if (_records.length === 0) throw new Error('No records to preview');
    const records = sortBy([..._records], record => record.measurementStart);
    const latestProtocol = records.at(-1)!.protocol;

    const positionNumColIndices = new Map<string, number>();
    let columnIdx = 0;
    latestProtocol.positions.forEach(position => {
        positionNumColIndices.set(position.positionNumber, columnIdx++);
    });

    //Get position data from protocol
    const positionNumbers: string[] = [];
    const nominalValues: string[] = [];
    const lowerTolerances: string[] = [];
    const upperTolerances: string[] = [];
    const gauges: string[] = [];
    latestProtocol.positions.forEach(position => {
        positionNumbers.push(position.positionNumber);
        nominalValues.push(position.nominal ?? '');
        lowerTolerances.push(position.lowerTolerance ?? '');
        upperTolerances.push(position.upperTolerance ?? '');
        gauges.push(position.type === 'measurement'
            ? position.toolType ?? ''
            : 'Visual'
        );
    });

    //Get record rows
    const recordRows = records.flatMap(record => {
        return record.parts.map((part, partIndex) => {
            const row: RecordRow = {
                includeInReport: true,
                partCount: record.measurementType === 'production'
                    ? (record as RecordFinal).partCount!
                    : partIndex + 1,
                isPartNumberHighlighted: false,
                values: Array.from({ length: positionNumbers.length }, () => ({ value: '', isHighlighted: false })),
                measurementStart: record.measurementStart!,
                comment: record.comment,
            };

            //Get actual values and highlight if they are out of tolerance; update includeInReport flag
            for (const recordPos of part.positionValues) {
                const columnIdx = positionNumColIndices.get(recordPos.positionNumber);
                if (columnIdx == null) continue;

                const position = record.protocol.positions.find(pos => pos.positionNumber === recordPos.positionNumber);
                if (position == null) continue;

                if (position.type === 'measurement') {
                    if (recordPos.value == null || recordPos.value === '') continue;
                    const outOfSpec = isOutOfSpec(position, recordPos);

                    row.values[columnIdx].value = recordPos.value as string;
                    row.values[columnIdx].isHighlighted = outOfSpec;
                    if (outOfSpec) row.includeInReport = false;
                } else {
                    if (recordPos.value == null) continue;
                    const isOk = recordPos.value as boolean;
                    row.values[columnIdx].value = isOk ? 'OK' : 'NOK';
                    row.values[columnIdx].isHighlighted = !isOk;
                    if (!isOk) row.includeInReport = false;
                }
            }

            return row;
        });
    });

    //Highlight part numbers, if they are not in ascending order
    for (let i = 1; i < recordRows.length; i++) {
        const prevRow = recordRows[i - 1];
        const currRow = recordRows[i];
        if (prevRow.partCount > currRow.partCount) {
            currRow.isPartNumberHighlighted = true;
        }
    }

    return {
        positionNumbers,
        nominalValues,
        lowerTolerances,
        upperTolerances,
        gauges,
        recordRows,
    };
}
